import { DataProvidersSettings } from './DataProvidersSettings'
import { CompanyIntelligencePanel } from './CompanyIntelligencePanel'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAccessContext } from '../../../context/AccessContext'
import { useI18n } from '../../../i18n/I18nContext'
import { navigateTo } from '../../../services/navigation'
import { listSystemTenants, listSystemUsers } from '../../../services/access'
import type { AccessTenant, GlobalUserDirectoryEntry } from '../../../types/access'
import { JobSourcesPanel } from './JobSourcesPanel'
import { MarketClaimsReviewPanel } from './MarketClaimsReviewPanel'
import { DataLifecycleWorkspace } from './DataLifecycleWorkspace'

const USERS_PAGE_SIZE = 25

type PlatformAreaStatus = 'connected' | 'not_connected' | 'tenant_required'

interface PlatformArea {
  key: string
  title: string
  description: string
  status: PlatformAreaStatus
  cta?: string
  onOpen?: () => void
}

const formatTenantOption = (tenant: AccessTenant) => tenant.name || tenant.slug || tenant.id

const userDisplayName = (user: GlobalUserDirectoryEntry) =>
  [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || user.phone || user.id

// True Platform Console: the landing surface for System Super Admin / platform
// admin identities. Everything rendered here is platform-safe (no tenant
// selection required). Tenant-owned operations are reached through the
// explicit "Manage tenant" action, which selects a tenant and opens the
// tenant Admin Workspace.
export const PlatformConsole: React.FC = () => {
  const { t } = useI18n()
  const { accessContext, setCurrentTenantId } = useAccessContext()
  const isPlatformUser = Boolean(
    accessContext?.isSystemAdmin
    || accessContext?.isSystemSuperAdmin
    || accessContext?.isPlatformAdmin
    || accessContext?.companyIntelligence?.view
    || accessContext?.canManageTenants
  )
  const canManageTenants = Boolean(accessContext?.isSystemSuperAdmin || accessContext?.canManageTenants)
  const selectedTenantId = accessContext?.tenantId || ''

  const [tenants, setTenants] = useState<AccessTenant[]>([])
  const [tenantsLoading, setTenantsLoading] = useState(false)
  const [tenantsError, setTenantsError] = useState('')

  const [users, setUsers] = useState<GlobalUserDirectoryEntry[]>([])
  const [usersTotal, setUsersTotal] = useState(0)
  const [usersOffset, setUsersOffset] = useState(0)
  const [usersLoading, setUsersLoading] = useState(false)
  const [usersError, setUsersError] = useState('')
  const [userSearch, setUserSearch] = useState('')
  const [userTenantFilter, setUserTenantFilter] = useState('')

  const loadTenants = useCallback(async () => {
    if (!canManageTenants) return
    setTenantsLoading(true)
    setTenantsError('')
    try {
      setTenants(await listSystemTenants())
    } catch (err: any) {
      setTenantsError(err?.response?.data?.message || t('Unable to load the tenant directory.'))
    } finally {
      setTenantsLoading(false)
    }
  }, [canManageTenants, t])

  const loadUsers = useCallback(async (offset: number) => {
    if (!canManageTenants) return
    setUsersLoading(true)
    setUsersError('')
    try {
      const result = await listSystemUsers({
        q: userSearch.trim() || undefined,
        tenantId: userTenantFilter || undefined,
        limit: USERS_PAGE_SIZE,
        offset,
      })
      setUsers(result.users)
      setUsersTotal(result.total)
      setUsersOffset(result.offset)
    } catch (err: any) {
      setUsersError(err?.response?.data?.message || t('Unable to load the global user directory.'))
    } finally {
      setUsersLoading(false)
    }
  }, [canManageTenants, t, userSearch, userTenantFilter])

  useEffect(() => {
    loadTenants()
  }, [loadTenants])

  useEffect(() => {
    loadUsers(0)
    // Reload from the first page whenever the tenant filter changes; free-text
    // search is applied explicitly via the Search button / Enter key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTenantFilter, canManageTenants])

  const manageTenant = (tenantId: string) => {
    setCurrentTenantId(tenantId)
    navigateTo('/admin')
  }

  const platformAreas = useMemo<PlatformArea[]>(() => {
    const areas: PlatformArea[] = [
    {
      key: 'tenants',
      title: t('Tenants'),
      description: t('Create, inspect, and enter tenant workspaces across the whole platform.'),
      status: 'connected',
      cta: t('View tenant directory'),
      onOpen: () => document.getElementById('platform-tenant-directory')?.scrollIntoView({ behavior: 'smooth' }),
    },
    ...(accessContext?.isSystemSuperAdmin ? [{ key: 'data-lifecycle', title: t('Data Lifecycle Management'),
      description: t('Manage reversible tenant access, global user access and tenant memberships.'), status: 'connected' as const,
      cta: t('Open lifecycle management'), onOpen: () => document.getElementById('platform-data-lifecycle')?.scrollIntoView({ behavior: 'smooth' }) }] : []),
    {
      key: 'global-users',
      title: t('Global Users'),
      description: t('Inspect global identities, tenant memberships, and system roles without selecting a tenant.'),
      status: 'connected',
      cta: t('View global users'),
      onOpen: () => document.getElementById('platform-global-users')?.scrollIntoView({ behavior: 'smooth' }),
    },
    {
      key: 'organizations',
      title: t('Organizations'),
      description: t('Cross-tenant organization directory and relationship tools.'),
      status: accessContext?.companyIntelligence?.view ? 'connected' : 'not_connected',
      cta: accessContext?.companyIntelligence?.view ? t('Open company intelligence') : undefined,
      onOpen: () => document.getElementById('platform-company-intelligence')?.scrollIntoView({ behavior: 'smooth' }),
    },
    {
      key: 'billing',
      title: t('Billing / Revenue'),
      description: t('Platform billing overview, subscriptions, and revenue.'),
      status: 'connected',
      cta: t('Open platform billing'),
      onOpen: () => navigateTo('/billing'),
    },
    {
      key: 'sales',
      title: t('Sales Administration'),
      description: t('Platform revenue attribution, commissions, and sales representative portfolios.'),
      status: 'connected',
      cta: t('Open Sales Administration'),
      onOpen: () => navigateTo('/admin?workspace=sales'),
    },
    {
      key: 'sales-crm',
      title: t('Sales CRM'),
      description: t('Prospects, contacts, opportunities and daily representative follow-ups.'),
      status: 'connected',
      cta: t('Open Sales CRM'),
      onOpen: () => navigateTo('/admin?workspace=sales-crm'),
    },
    {
      key: 'platform-roles',
      title: t('Platform Roles & Permissions'),
      description: t('System-level roles and platform permission assignments.'),
      status: 'not_connected',
    },
    {
      key: 'job-sources',
      title: t('Job Sources'),
      description: t('Configure, test, synchronize, and monitor external Market Demand providers.'),
      status: 'connected',
      cta: t('Manage job sources'),
      onOpen: () => document.getElementById('platform-job-sources')?.scrollIntoView({ behavior: 'smooth' }),
    },
    {
      key: 'market-claims',
      title: t('Market Employer Claims'),
      description: t('Review company ownership and representation claims for discovered employers.'),
      status: 'connected',
      cta: t('Review claims'),
      onOpen: () => document.getElementById('platform-market-claims')?.scrollIntoView({ behavior: 'smooth' }),
    },
    {
      key: 'platform-settings',
      title: t('Platform Settings'),
      description: t('Installation-wide defaults and platform configuration.'),
      status: 'connected',
      cta: t('Manage Data Providers'),
      onOpen: () => document.getElementById('platform-data-providers')?.scrollIntoView({behavior:'smooth'}),
    },
    {
      key: 'diagnostics',
      title: t('System Diagnostics'),
      description: t('Runtime health, storage checks, and platform observability.'),
      status: 'not_connected',
    },
    {
      key: 'branding-defaults',
      title: t('Global Branding Defaults'),
      description: t('Default branding applied before tenants customize their own.'),
      status: 'not_connected',
    },
    {
      key: 'skills',
      title: t('Global Skill Catalog'),
      description: t('Canonical skills shared by jobs, worker profiles, and learning.'),
      status: 'not_connected',
    },
    {
      key: 'learning-catalog',
      title: t('Learning & Mentor Catalog'),
      description: t('Global AI mentor templates, starter tracks, and platform learning defaults. Tenant mentorship operations stay in the tenant Mentor Admin workspace.'),
      status: 'tenant_required',
      cta: t('Open Tenant Mentor Admin'),
      onOpen: () => navigateTo('/mentor-admin'),
    },
    ]
    return areas.filter((area) => area.key !== 'market-claims' || Boolean(accessContext?.isSystemSuperAdmin))
  }, [t, accessContext?.isSystemSuperAdmin, accessContext?.companyIntelligence?.view])

  if (accessContext && !isPlatformUser) {
    return (
      <section className="panel workspace-panel stack" data-testid="platform-console">
        <h2 className="heading-lg">{t('Platform Console')}</h2>
        <p className="text-muted">{t('You are signed in, but this account does not have platform administration access.')}</p>
      </section>
    )
  }

  return (
    <section className="panel workspace-panel stack" data-testid="platform-console">
      {accessContext?.companyIntelligence?.view ? <CompanyIntelligencePanel tenantId={selectedTenantId} capabilities={accessContext.companyIntelligence} /> : null}
      <DataLifecycleWorkspace isSuperAdmin={Boolean(accessContext?.isSystemSuperAdmin)} currentUserId={accessContext?.currentUser?.id} />
      <div className="stack" style={{ gap: '.4rem' }}>
        <h2 className="heading-lg">{t('Platform Console')}</h2>
        <p className="text-muted">
          {t('System-wide administration for this platform installation. This is not tenant administration: select a tenant only when you need to manage tenant-owned data.')}
        </p>
        <div className="inline-group" style={{ alignItems: 'center', gap: '.5rem' }}>
          <span className="status-pill status-pill--approved">{t('Platform scope — all tenants')}</span>
          {selectedTenantId ? (
            <span className="helper" data-testid="platform-selected-tenant-note">
              {t('A tenant is currently selected for tenant-owned tools.')}
            </span>
          ) : null}
        </div>
      </div>

      <div className="stack">
        <h3 style={{ margin: 0 }}>{t('Platform areas')}</h3>
        <div className="card-grid" data-testid="platform-area-grid">
          {platformAreas.map((area) => (
            <article key={area.key} className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }} data-testid={`platform-area-${area.key}`}>
              <h4 style={{ margin: 0 }}>{area.title}</h4>
              <p className="text-muted" style={{ margin: 0 }}>{area.description}</p>
              {area.status === 'not_connected' ? (
                <span className="helper">{t('Not connected yet — available after the platform endpoint is connected.')}</span>
              ) : null}
              {area.status === 'tenant_required' ? (
                <span className="helper">{t('Global catalog console is not connected yet. Tenant-specific mentorship requires selecting a tenant.')}</span>
              ) : null}
              {area.cta && area.onOpen ? (
                <button type="button" className="btn btn-secondary" onClick={area.onOpen}>{area.cta}</button>
              ) : null}
            </article>
          ))}
        </div>
      </div>

      <DataProvidersSettings />
      <JobSourcesPanel />
      <MarketClaimsReviewPanel />

      <div className="stack" id="platform-tenant-directory" data-testid="platform-tenant-directory">
        <div className="inline-group" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>{t('Tenant management')}</h3>
          <button type="button" className="btn btn-ghost" onClick={loadTenants} disabled={tenantsLoading}>
            {tenantsLoading ? t('Refreshing…') : t('Refresh tenants')}
          </button>
        </div>
        <p className="helper" style={{ margin: 0 }}>
          {t('Select a tenant to manage tenant-owned data — workers, jobs, locations, timesheets, tenant billing, tenant branding, and tenant mentorship.')}
        </p>
        {!canManageTenants ? (
          <p className="helper">{t('The tenant directory requires platform tenant-management access.')}</p>
        ) : null}
        {tenantsError ? <p className="error-text">{tenantsError}</p> : null}
        {tenantsLoading && tenants.length === 0 ? <p className="helper">{t('Loading tenants…')}</p> : null}
        {canManageTenants && !tenantsLoading && !tenantsError && tenants.length === 0 ? (
          <p className="helper" data-testid="platform-no-tenants">
            {t('No tenants exist yet. Create the first tenant from the Admin Workspace tenant setup card.')}
          </p>
        ) : null}
        {tenants.length > 0 ? (
          <div className="panel" style={{ overflowX: 'auto' }}>
            <table className="workspace-table" data-testid="platform-tenant-table">
              <thead>
                <tr>
                  <th>{t('Tenant')}</th>
                  <th>{t('Slug')}</th>
                  <th>{t('Status')}</th>
                  <th>{t('Industry')}</th>
                  <th>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <tr key={tenant.id} data-testid={`platform-tenant-row-${tenant.slug || tenant.id}`}>
                    <td>{tenant.name || tenant.slug || tenant.id}</td>
                    <td>{tenant.slug || '—'}</td>
                    <td>{tenant.status || '—'}</td>
                    <td>{tenant.industry_type || tenant.industryType || t('General')}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => manageTenant(tenant.id)}
                        data-testid={`platform-manage-tenant-${tenant.slug || tenant.id}`}
                      >
                        {selectedTenantId === tenant.id ? t('Open tenant admin') : t('Manage tenant')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <div className="stack" id="platform-global-users" data-testid="platform-global-users">
        <h3 style={{ margin: 0 }}>{t('Global Users')}</h3>
        <p className="helper" style={{ margin: 0 }}>
          {t('Read-only platform directory of global identities. Tenant is a filter here, never a required gate; tenant-scoped user changes happen inside a selected tenant.')}
        </p>
        <form
          className="inline-group"
          style={{ alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}
          onSubmit={(event) => { event.preventDefault(); loadUsers(0) }}
        >
          <input
            className="input"
            value={userSearch}
            onChange={(event) => setUserSearch(event.target.value)}
            placeholder={t('Search by email, phone, or name')}
            aria-label={t('Search global users')}
            data-testid="platform-user-search"
          />
          <select
            className="input"
            value={userTenantFilter}
            onChange={(event) => setUserTenantFilter(event.target.value)}
            aria-label={t('Filter users by tenant')}
            data-testid="platform-user-tenant-filter"
          >
            <option value="">{t('All tenants')}</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>{formatTenantOption(tenant)}</option>
            ))}
          </select>
          <button type="submit" className="btn btn-secondary" disabled={usersLoading}>
            {usersLoading ? t('Searching…') : t('Search')}
          </button>
        </form>
        {!canManageTenants ? (
          <p className="helper">{t('The global user directory requires platform tenant-management access.')}</p>
        ) : null}
        {usersError ? <p className="error-text">{usersError}</p> : null}
        {usersLoading && users.length === 0 ? <p className="helper">{t('Loading global users…')}</p> : null}
        {canManageTenants && !usersLoading && !usersError && users.length === 0 ? (
          <p className="helper" data-testid="platform-no-users">{t('No users match the current filters.')}</p>
        ) : null}
        {users.length > 0 ? (
          <div className="panel" style={{ overflowX: 'auto' }}>
            <table className="workspace-table" data-testid="platform-user-table">
              <thead>
                <tr>
                  <th>{t('User')}</th>
                  <th>{t('System role')}</th>
                  <th>{t('Tenant memberships')}</th>
                  <th>{t('Worker profile')}</th>
                  <th>{t('Status')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="stack" style={{ gap: '.15rem' }}>
                        <strong>{userDisplayName(user)}</strong>
                        <span className="helper">{user.email || user.phone || user.id}</span>
                      </div>
                    </td>
                    <td>{user.system_role ? user.system_role.replace(/_/g, ' ') : '—'}</td>
                    <td>
                      {(user.tenant_memberships || []).length === 0
                        ? t('No tenant membership (global identity)')
                        : (user.tenant_memberships || []).map((membership) => (
                            <div key={`${user.id}-${membership.tenant_id}`} className="helper">
                              {membership.tenant_name || membership.tenant_slug || membership.tenant_id}
                              {' — '}
                              {(membership.membership_type || '').replace(/_/g, ' ') || t('member')}
                              {membership.status && membership.status !== 'active' ? ` (${membership.status})` : ''}
                            </div>
                          ))}
                    </td>
                    <td>{user.has_worker_profile ? t('Yes') : '—'}</td>
                    <td>{user.active === false || user.status === 'disabled' ? t('Inactive') : t('Active')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {usersTotal > USERS_PAGE_SIZE ? (
          <div className="inline-group" style={{ alignItems: 'center', gap: '.5rem' }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => loadUsers(Math.max(usersOffset - USERS_PAGE_SIZE, 0))}
              disabled={usersLoading || usersOffset === 0}
            >
              {t('Previous')}
            </button>
            <span className="helper">
              {`${usersOffset + 1}–${Math.min(usersOffset + USERS_PAGE_SIZE, usersTotal)} / ${usersTotal}`}
            </span>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => loadUsers(usersOffset + USERS_PAGE_SIZE)}
              disabled={usersLoading || usersOffset + USERS_PAGE_SIZE >= usersTotal}
            >
              {t('Next')}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  )
}
